import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  createStockNewsActionItem,
  deleteStockNewsActionItem,
  duplicateStockNewsActionItem,
  removeStockNewsActionItemLabel,
  renameStockNewsActionItemLabel,
  updateStockNewsActionItem,
} from "../../shared/api/stockNewsActionItems";
import EntityLabelCell from "../entity/EntityLabelCell";
import EntityLabelFilterBar from "../entity/EntityLabelFilterBar";
import EntityLabelsModal from "../entity/EntityLabelsModal";
import EntityListBulkToolbar from "../entity/EntityListBulkToolbar";
import {
  getEntityListItemId,
  useEntityLabelFilter,
  useEntityListSelection,
} from "../../shared/hooks/useEntityLabelFilter";
import { getMessageForFetchError } from "../../shared/utils/apiError";
import { bulkApplyLabelsToItems, getSharedLabelsAmongItems } from "../../shared/utils/entityBulkLabels";
import { bulkSequentialDelete } from "../../shared/utils/bulkSequentialDelete";
import { formatBatchSummaryMessage } from "../../shared/utils/batchSummaryMessage";
import {
  entityLabelsEqual,
  ENTITY_LABEL_FILTER_UNLABELED_DISPLAY,
  getItemLabels,
  isUnlabeledEntityLabelFilter,
  normalizeEntityLabelsArray,
} from "../../shared/utils/entityLabelFilter";
import { IconEdit, IconDelete, IconDuplicate, IconClose, IconReopen, iconBtnStyle, RowActionIcons } from "../../shared/icons/IconLib";
import { TableActionColumnHeader } from "../TableShell";
import StockDailyNewsInlineEditField from "./StockDailyNewsInlineEditField";

function StockDailyNewsActionItems({
  stockSession,
  serverUrl,
  actionItems = [],
  loading = false,
  onActionItemsChanged,
  onError,
  setConfirmDialog,
}) {
  const items = Array.isArray(actionItems) ? actionItems : [];
  const {
    activeLabel,
    setActiveLabel,
    labelOptions,
    filteredItems: filteredActionItems,
    labelsForNewItem,
    addLabelName,
    renameLabelName,
    removeLabelName,
  } = useEntityLabelFilter({ items });
  const {
    selectedIds,
    handleSelectAll,
    handleSelectOne,
    clearSelection,
  } = useEntityListSelection(getEntityListItemId);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bulkLabelsOpen, setBulkLabelsOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editBuffer, setEditBuffer] = useState("");

  useEffect(() => {
    clearSelection();
  }, [activeLabel, clearSelection]);

  const selectedItems = useMemo(
    () => items.filter((item) => selectedIds.has(item.id)),
    [items, selectedIds]
  );

  const sharedBulkLabels = useMemo(
    () => getSharedLabelsAmongItems(selectedItems),
    [selectedItems]
  );

  const handleAdd = useCallback(
    async (e) => {
      e?.preventDefault();
      const title = (draft || "").trim();
      if (!title) {
        setConfirmDialog({
          open: true,
          title: "Add action item",
          message: "Enter an action item before adding.",
          mode: "alert",
          onConfirm: null,
        });
        return;
      }
      setSaving(true);
      try {
        await createStockNewsActionItem(serverUrl, stockSession, title, {
          labels: labelsForNewItem,
        });
        setDraft("");
        await onActionItemsChanged();
      } catch (err) {
        onError(err.message || "Failed to add action item");
      } finally {
        setSaving(false);
      }
    },
    [draft, labelsForNewItem, onActionItemsChanged, onError, serverUrl, setConfirmDialog, stockSession]
  );

  const handleAddLabel = useCallback(
    (name) => {
      const result = addLabelName(name);
      if (!result.ok) {
        setConfirmDialog({
          open: true,
          title: "Add label",
          message: result.error,
          mode: "alert",
          onConfirm: null,
        });
      }
      return result;
    },
    [addLabelName, setConfirmDialog]
  );

  const handleRenameLabel = useCallback(
    async (oldLabel, newLabel) => {
      setSaving(true);
      try {
        await renameStockNewsActionItemLabel(serverUrl, stockSession, { oldLabel, newLabel });
        const result = renameLabelName(oldLabel, newLabel);
        if (!result.ok) {
          throw new Error(result.error || "Failed to rename label");
        }
        await onActionItemsChanged();
      } catch (err) {
        onError(err.message || "Failed to rename label");
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [onActionItemsChanged, onError, renameLabelName, serverUrl, stockSession]
  );

  const handleRemoveLabel = useCallback(
    async (label) => {
      setSaving(true);
      try {
        await removeStockNewsActionItemLabel(serverUrl, stockSession, label);
        const result = removeLabelName(label);
        if (!result.ok) {
          throw new Error(result.error || "Failed to remove label");
        }
        await onActionItemsChanged();
      } catch (err) {
        onError(err.message || "Failed to remove label");
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [onActionItemsChanged, onError, removeLabelName, serverUrl, stockSession]
  );

  const handleApplyLabelsForItem = useCallback(
    async (item, labels) => {
      if (editingId === item.id || saving) return;
      const nextLabels = normalizeEntityLabelsArray(labels);
      if (entityLabelsEqual(getItemLabels(item), nextLabels)) {
        return;
      }
      setSaving(true);
      try {
        await updateStockNewsActionItem(serverUrl, stockSession, {
          actionItemId: item.id,
          labels: nextLabels,
        });
        await onActionItemsChanged();
      } catch (err) {
        onError(err.message || "Failed to update labels");
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [editingId, onActionItemsChanged, onError, saving, serverUrl, stockSession]
  );

  const startEdit = useCallback((item) => {
    setEditingId(item.id);
    setEditBuffer(item.title || "");
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditBuffer("");
  }, []);

  const commitEdit = useCallback(async () => {
    const title = (editBuffer || "").trim();
    if (!editingId) return;
    if (!title) {
      setConfirmDialog({
        open: true,
        title: "Edit action item",
        message: "Action item text cannot be empty.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setSaving(true);
    try {
      await updateStockNewsActionItem(serverUrl, stockSession, {
        actionItemId: editingId,
        title,
      });
      cancelEdit();
      await onActionItemsChanged();
    } catch (err) {
      onError(err.message || "Failed to update action item");
    } finally {
      setSaving(false);
    }
  }, [
    cancelEdit,
    editBuffer,
    editingId,
    onActionItemsChanged,
    onError,
    serverUrl,
    setConfirmDialog,
    stockSession,
  ]);

  const handleToggleDone = useCallback(
    async (item) => {
      if (editingId === item.id || saving) return;
      setSaving(true);
      try {
        await updateStockNewsActionItem(serverUrl, stockSession, {
          actionItemId: item.id,
          status: item.status === "done" ? "open" : "done",
        });
        await onActionItemsChanged();
      } catch (err) {
        onError(err.message || "Failed to update action item");
      } finally {
        setSaving(false);
      }
    },
    [editingId, onActionItemsChanged, onError, saving, serverUrl, stockSession]
  );

  const handleDuplicate = useCallback(
    async (item) => {
      if (editingId === item.id || saving) return;
      setSaving(true);
      try {
        await duplicateStockNewsActionItem(serverUrl, stockSession, item.id);
        await onActionItemsChanged();
      } catch (err) {
        onError(err.message || "Failed to duplicate action item");
      } finally {
        setSaving(false);
      }
    },
    [editingId, onActionItemsChanged, onError, saving, serverUrl, stockSession]
  );

  const handleDelete = useCallback(
    (item) => {
      setConfirmDialog({
        open: true,
        title: "Delete action item",
        mode: "confirm",
        message: `Delete this action item?\n\n"${item.title}"`,
        onConfirm: async () => {
          setSaving(true);
          try {
            await deleteStockNewsActionItem(serverUrl, stockSession, item.id);
            if (editingId === item.id) cancelEdit();
            await onActionItemsChanged();
          } catch (err) {
            onError(err.message || "Failed to delete action item");
          } finally {
            setSaving(false);
          }
        },
      });
    },
    [
      cancelEdit,
      editingId,
      onActionItemsChanged,
      onError,
      serverUrl,
      setConfirmDialog,
      stockSession,
    ]
  );

  const openBulkLabels = useCallback(() => {
    if (selectedIds.size === 0) {
      setConfirmDialog({
        open: true,
        title: "Update labels",
        message: "Please select at least one action item to update.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setBulkLabelsOpen(true);
  }, [selectedIds.size, setConfirmDialog]);

  const handleBulkApplyLabels = useCallback(
    async (labels) => {
      setSaving(true);
      try {
        const { updated, skipped, errors } = await bulkApplyLabelsToItems({
          items,
          selectedIds,
          labels,
          getId: getEntityListItemId,
          updateLabels: async (item, nextLabels) => {
            await updateStockNewsActionItem(serverUrl, stockSession, {
              actionItemId: item.id,
              labels: nextLabels,
            });
          },
        });
        await onActionItemsChanged();
        if (errors.length > 0) {
          setConfirmDialog({
            open: true,
            title: "Bulk label update",
            message: formatBatchSummaryMessage(
              `Updated ${updated} action item(s) (${skipped} unchanged).`,
              updated + skipped,
              errors.length,
              errors
            ),
            mode: "alert",
            onConfirm: null,
          });
        }
        clearSelection();
        setBulkLabelsOpen(false);
      } catch (err) {
        onError(err.message || "Failed to update labels");
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [
      clearSelection,
      items,
      onActionItemsChanged,
      onError,
      selectedIds,
      serverUrl,
      setConfirmDialog,
      stockSession,
    ]
  );

  const deleteSelected = useCallback(() => {
    if (selectedIds.size === 0) {
      setConfirmDialog({
        open: true,
        title: "Delete action items",
        message: "Please select at least one action item to delete.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete action items",
      mode: "confirm",
      message: `Are you sure you want to delete ${selectedIds.size} action item${selectedIds.size === 1 ? "" : "s"}?`,
      onConfirm: async () => {
        setDeleting(true);
        const idsToDelete = Array.from(selectedIds);
        const { successCount, errorCount, errors } = await bulkSequentialDelete(
          idsToDelete,
          async (actionItemId) => {
            try {
              await deleteStockNewsActionItem(serverUrl, stockSession, actionItemId);
              return { ok: true, body: "" };
            } catch (err) {
              return { ok: false, body: err?.message || "Failed to delete action item" };
            }
          },
          getMessageForFetchError
        );
        if (editingId && idsToDelete.includes(editingId)) cancelEdit();
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message: formatBatchSummaryMessage("Delete completed!", successCount, errorCount, errors),
          mode: "alert",
          onConfirm: null,
        });
        clearSelection();
        await onActionItemsChanged();
        setDeleting(false);
      },
    });
  }, [
    cancelEdit,
    clearSelection,
    editingId,
    onActionItemsChanged,
    selectedIds,
    serverUrl,
    setConfirmDialog,
    stockSession,
  ]);

  return (
    <div className="panel stock-daily-news-panel">
      <div className="panel-title" style={{ marginBottom: 4 }}>
        Action items
      </div>
      <p className="panel-hint" style={{ marginTop: 0, marginBottom: 12 }}>
        Capture follow-ups after reading the news. Mark items done when complete.
      </p>

      <EntityLabelFilterBar
        items={items}
        activeLabel={activeLabel}
        onActiveLabelChange={setActiveLabel}
        labelOptions={labelOptions}
        ariaLabel="Action item labels"
        showUnlabeledFilter
      />

      <form onSubmit={handleAdd} style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
        <input
          type="text"
          className="form-control"
          style={{ flex: "1 1 280px", minWidth: 0 }}
          placeholder="Add an action item to track…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={saving}
          maxLength={500}
        />
        <button type="submit" className="btn btn-md btn-primary" disabled={saving}>
          {saving ? "Saving…" : "Add action item"}
        </button>
      </form>

      <EntityListBulkToolbar
        selectedCount={selectedIds.size}
        onUpdateLabels={openBulkLabels}
        onDeleteSelected={deleteSelected}
        updating={saving && bulkLabelsOpen}
        deleting={deleting}
        ariaLabel="Bulk action item actions"
      />

      {loading ? (
        <p className="panel-hint">Loading action items…</p>
      ) : items.length === 0 ? (
        <p className="panel-hint">No action items yet. Add one above after reviewing news.</p>
      ) : filteredActionItems.length === 0 ? (
        <p className="panel-hint">
          {isUnlabeledEntityLabelFilter(activeLabel)
            ? `No ${ENTITY_LABEL_FILTER_UNLABELED_DISPLAY.toLowerCase()} action items. Assign labels from the label icon in Action or choose All.`
            : `No action items with label “${activeLabel}”. Assign this label from the label icon in Action or choose All.`}
        </p>
      ) : (
        <div className="table-scroll">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th className="table-col-select">
                    <input
                      type="checkbox"
                      checked={
                        filteredActionItems.length > 0 &&
                        filteredActionItems.every((item) => selectedIds.has(item.id))
                      }
                      onChange={(e) => handleSelectAll(e, filteredActionItems)}
                      disabled={filteredActionItems.length === 0 || saving || deleting}
                      aria-label="Select all action items"
                    />
                  </th>
                  <th className="table-col-primary">Action item</th>
                  <TableActionColumnHeader />
                </tr>
              </thead>
              <tbody>
                {filteredActionItems.map((item) => {
                  const isEditing = editingId === item.id;
                  const isDone = item.status === "done";
                  return (
                    <tr
                      key={item.id}
                      className={isDone ? "stock-daily-news-row--closed" : undefined}
                    >
                      <td className="table-col-select">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.id)}
                          onChange={(e) => handleSelectOne(item.id, e.target.checked)}
                          disabled={isEditing || saving || deleting}
                          aria-label="Select action item"
                        />
                      </td>
                      <td className="table-col-primary">
                        {isEditing ? (
                          <StockDailyNewsInlineEditField
                            value={editBuffer}
                            onChange={(e) => setEditBuffer(e.target.value)}
                            disabled={saving}
                            maxLength={500}
                            onSave={commitEdit}
                            onCancel={cancelEdit}
                          />
                        ) : (
                          <span className="stock-daily-news-item-label">
                            {item.title}
                          </span>
                        )}
                      </td>
                      <td className="text-center table-col-action">
                        {isEditing ? (
                          <span style={{ display: "inline-flex", gap: 8 }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              disabled={saving}
                              onClick={() => commitEdit()}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              disabled={saving}
                              onClick={cancelEdit}
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <RowActionIcons>
                            <EntityLabelCell
                              item={item}
                              iconOnly
                              labelOptions={labelOptions}
                              disabled={isEditing}
                              saving={saving}
                              ariaLabel="Action item labels"
                              onApplyLabels={(labels) => handleApplyLabelsForItem(item, labels)}
                              onAddLabel={handleAddLabel}
                              onRenameLabel={handleRenameLabel}
                              onRemoveLabel={handleRemoveLabel}
                              setConfirmDialog={setConfirmDialog}
                            />
                            <button
                              type="button"
                              onClick={() => handleDuplicate(item)}
                              className="btn-link"
                              style={iconBtnStyle}
                              title="Duplicate"
                              disabled={saving}
                            >
                              <IconDuplicate size={18} />
                            </button>
                            <button
                              type="button"
                              onClick={() => startEdit(item)}
                              className="btn-link"
                              style={iconBtnStyle}
                              title="Edit"
                              disabled={saving}
                            >
                              <IconEdit size={18} />
                            </button>
                            {isDone ? (
                              <button
                                type="button"
                                onClick={() => handleToggleDone(item)}
                                className="btn-link"
                                style={iconBtnStyle}
                                title="Reopen"
                                disabled={saving}
                              >
                                <IconReopen size={18} />
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleToggleDone(item)}
                                className="btn-link-muted"
                                style={iconBtnStyle}
                                title="Mark done"
                                disabled={saving}
                              >
                                <IconClose size={18} />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDelete(item)}
                              className="btn-link-danger"
                              style={iconBtnStyle}
                              title="Delete"
                              disabled={saving}
                            >
                              <IconDelete size={18} />
                            </button>
                          </RowActionIcons>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <EntityLabelsModal
        open={bulkLabelsOpen}
        onClose={() => setBulkLabelsOpen(false)}
        labelOptions={labelOptions}
        selectedLabels={sharedBulkLabels}
        onApplyLabels={handleBulkApplyLabels}
        onAddLabel={handleAddLabel}
        onRenameLabel={handleRenameLabel}
        onRemoveLabel={handleRemoveLabel}
        setConfirmDialog={setConfirmDialog}
        saving={saving}
        subtitle={`Select labels to apply to ${selectedIds.size} selected action item${selectedIds.size === 1 ? "" : "s"}. Rename or delete from the same row.`}
      />
    </div>
  );
}

export default StockDailyNewsActionItems;
